import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { motion } from 'framer-motion';
import React, { useId } from 'react';
import { LiaTimesCircle } from 'react-icons/lia';
import { RiSearchLine } from 'react-icons/ri';

import InputError from './InputError';
import { InputSearchProps } from './InputSearch';

import { cn, ONLY_LETTERS } from '@/utils';
import { FADE_IN_OUT, inputTextBaseClass } from '@/utils/constants';

export interface ItemAutocomplete {
  id: number;
  value: string;
}

interface DefaultProps extends InputSearchProps {
  list: ItemAutocomplete[];
  handleInputSelect: (value: string) => void;
}

export const InputAutoComplete = React.forwardRef<
  HTMLInputElement,
  DefaultProps
>((props, ref) => {
  const id = useId();
  const alertId = useId();

  const {
    name,
    value,
    disabled,
    placeholder,
    handleCancel,
    onSubmit,
    onChange,
    onlyLetters,
    handleInputSelect,
    list,
    error,
    success,
    ...rest
  } = props;

  const idLabelFor = `search-input-${id}`;

  const inputClass = cn(inputTextBaseClass, 'pl-[40px]', {
    'border-gdt-status-red': error,
    'border-gdt-status-green bg-gdt-status-green-light': success,
    'text-gdt-gray-4 border-gdt-gray-5 bg-gdt-gray-6': disabled,
    'pr-[90px] ': value.length > 0,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onlyLetters) {
      if (ONLY_LETTERS.test(e.target.value) || e.target.value === '') {
        onChange(e);
      }
    } else {
      onChange(e);
    }
  };

  return (
    <motion.div animate={'show'} initial={'hide'} variants={FADE_IN_OUT}>
      <fieldset className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 z-[1]">
          <RiSearchLine
            className={cn('text-gdt-blue w-5 h-5', {
              'opacity-20': disabled,
            })}
          />
        </div>
        <Combobox onChange={handleInputSelect}>
          <div className="relative mt-1">
            <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm">
              <ComboboxInput
                ref={ref}
                aria-describedby={alertId}
                aria-invalid={error ? 'true' : 'false'}
                aria-label="Ingresa tu busqueda acá"
                aria-labelledby={id}
                className={inputClass}
                disabled={disabled}
                id={idLabelFor}
                name={name}
                placeholder={placeholder}
                title="Busqueda general"
                value={value}
                onChange={handleInputChange}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    onSubmit && onSubmit(event);
                  }
                }}
                {...rest}
              />
            </div>
            {list.length > 0 && value.length >= 3 ? (
              <ComboboxOptions className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-[1]">
                {list.map((item: ItemAutocomplete) => {
                  return (
                    <ComboboxOption
                      key={item.id}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                          active ? 'bg-gdt-blue text-white' : 'text-gray-900'
                        }`
                      }
                      value={item.value}
                    >
                      {({ selected }) => (
                        <>
                          <span
                            className={`block truncate ${
                              selected ? 'font-medium' : 'font-normal'
                            }`}
                          >
                            {item.value}
                          </span>
                        </>
                      )}
                    </ComboboxOption>
                  );
                })}
              </ComboboxOptions>
            ) : (
              <></>
            )}
          </div>
        </Combobox>
        {value.length > 0 && (
          <button
            className="absolute inset-y-0 right-0 flex items-center gap-1 pr-3 z-[1]"
            type="button"
            onClick={handleCancel}
          >
            <div>
              <LiaTimesCircle className="w-5 h-5 text-gdt-gray-5" />
            </div>
            <span className="inline-block text-gdt-gray-4 text-2xs">
              Cancelar
            </span>
          </button>
        )}
      </fieldset>
      {error && <InputError.Label id={alertId}>{error}</InputError.Label>}
    </motion.div>
  );
});

InputAutoComplete.displayName = 'InputAutoComplete';
